import { Lock, Sparkles } from "lucide-react";
import { useEntitlements } from "@/context/EntitlementsContext";
import { Link } from "react-router-dom";

/**
 * Wrap a feature that requires a specific entitlement flag. If unlocked,
 * children render normally. Otherwise a "🔒 Pro Feature" badge with a
 * Go Pro button is shown.
 *
 * Usage:
 *   <LockedFeature flag="advanced_health" title="Advanced Health Score">
 *     <HealthCard />
 *   </LockedFeature>
 */
export default function LockedFeature({ flag: flagKey, title = "Pro feature", note, children, className = "" }) {
  const { flag } = useEntitlements();
  if (flag(flagKey)) return children;

  return (
    <div
      className={"relative rounded-xl border border-dashed border-[var(--border-line)] bg-[var(--bg-surface)] p-6 text-center " + className}
      data-testid={`locked-feature-${flagKey}`}
    >
      <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#FF5A1F]">
        <Lock size={12} /> Pro Feature
      </div>
      <h3 className="font-display text-lg font-bold mt-2">{title}</h3>
      {note && <p className="mt-1 text-sm text-[var(--text-muted)]">{note}</p>}
      <Link
        to="/profile?tab=subscription#manage-subscription"
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-sm font-semibold"
        data-testid={`go-pro-${flagKey}`}
      >
        <Sparkles size={14} /> Go Pro
      </Link>
    </div>
  );
}

export function LockBadge({ testid = "lock-badge" }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F] border border-[#FF5A1F]/30 uppercase tracking-wider"
      title="Requires Pro"
    >
      <Lock size={9} /> Pro
    </span>
  );
}
